/**
 * Everything around the canvas: the top bar's chips, the phase band, the rail under the canvas and
 * the words the day dock echoes.
 *
 * All of it is identical whether the trainer is looking at the weekly grid or the sequence canvas —
 * the same plan, the same phases, the same six muscle bars, the same lock — so it is built once and
 * each canvas composes it. What differs is stated as parameters rather than branched on inside: the
 * rail's caption names the unit, and the pattern row's countable half is present on one canvas and
 * absent on the other.
 *
 * ── The legend is GONE (13 September 2026) ──────────────────────────────────────────────────
 * `LegendView` and the `LegendBar` that read it existed for ONE entry — the sequence canvas's
 * dashed gap column carrying a bare number, which nothing wrote out in words. The 07.09 sequence
 * mock draws that column with "PRZERWA" over "bez przerwy" / "2 dni", so the code the legend
 * explained is now written on the thing itself and the row has nothing left to say. The week had
 * already lost its own entries for the same reason.
 *
 * **A component below this reads only the view model.** No `t`, no `Intl`, no arithmetic, no
 * `draft.phases[i]`.
 */

import type {
  MuscleLoad,
  PhaseSummary,
  PlanDraft,
} from '~/entities/plan-draft';
import {
  COLLECTION_CAPS,
  dayAdditionRefusal,
  dayDuplicationRefusal,
  isLivePlan,
  livePlanLock,
} from '~/entities/plan-draft';

import {
  toPhaseAddMenu,
  toPhaseEditor,
  type PhaseAddMenuView,
  type PhaseEditorView,
  type PhaseStepView,
} from './phaseEditorView';
import { KEY, unitNoun, type Translate } from './builderCopy';
import { toVolumeRail, type VolumeRailView } from './volumeStatus';

export interface PhaseTabView {
  /** Zero-based, for `update({ phase: index + 1 })`. */
  readonly index: number;
  readonly name: string;
  /** "tyg. 1–4" on the grid, "cykle 1–3" / "cykl 7" on the tape. */
  readonly range: string;
  /** "Build" / "Deload". */
  readonly tag: string;
  readonly isActive: boolean;
  /**
   * Which pip the tab carries. `deload` is a structural label and takes precedence — a deload phase
   * says so whether or not it is the one in view. `accent` marks the phase in view on any other
   * kind of phase. Everything else is neutral.
   */
  readonly pip: 'accent' | 'deload' | 'neutral';
}

/**
 * The band between the phase strip and the canvas, on BOTH canvases.
 *
 * ── One view, one component, because both mocks draw one band ───────────────────────────────
 * `Builder planu - tydzien - programator` and `Builder planu sekwencja` draw the same 44px row with
 * the same four parts: a title, a `--good-soft` pill, a spacer, and a `--text-3` tabular summary
 * hard right. What differs is what stands INSIDE the pill — the week's "↻ powtarza się − ×3 + ·
 * tyg. 1–3" against the sequence's static "↻ zapętlony · niezależny od kalendarza". That is view
 * ADR §2's case exactly: one component, the unit as a parameter.
 *
 * It was `RepeatingWeekView` and a `RepeatWeekBar` that only the week mounted. A sibling component
 * for the sequence row would have been two files carrying one 44px band, which is the near-duplicate
 * the styling rules exist to stop.
 */
export interface PatternBarView {
  /** "Wzór powtarzalnego tygodnia" / "Wzór powtarzalnego cyklu" — the mock's own words, verbatim. */
  readonly title: string;
  /**
   * The COUNTABLE half of the pill, or `null` where the pattern has no count to state.
   *
   * One nullable object rather than five nullable fields, because the five are present or absent
   * together: a canvas either repeats a pattern a stated number of times or it does not. The
   * sequence canvas is the second case — a phase's cycle count is set in the phase band there, and
   * the mock draws no stepper in this row.
   */
  readonly repeat: PatternRepeatView | null;
  /**
   * The pill's static sentence — "zapętlony · niezależny od kalendarza" — or `null` on the week.
   *
   * Exactly one of `repeat` and `note` is non-null. They are not merged into a discriminated union
   * because the pill's OTHER bytes (the ↻, the ground, the ink) are the same in both states and a
   * union would make the component reach into a variant to draw them.
   */
  readonly note: string | null;
  /**
   * "4 dni treningowe · 3 przerwy" on the week, "3 dni treningowe · cykl 4 dni" on the sequence
   * canvas — the pattern's shape, pushed to the band's right edge.
   */
  readonly summary: string;
}

/** The week's "powtarza się − ×3 + · tyg. 1–3": a count, its two ends, and the phase's span. */
export interface PatternRepeatView {
  /** "powtarza się" — the pill's own word, between the ↻ and the count. */
  readonly repeatsLabel: string;
  /** "×3". */
  readonly count: string;
  /**
   * "Powtórzenia tygodnia: 3" — what the live region says when a press changes the count.
   *
   * The visible "×3" is a glyph and two digits; read aloud on its own it is "times three", which
   * names neither the thing being counted nor the phase it belongs to.
   */
  readonly countLabel: string;
  /** "tyg. 1–3" — the phase's own span, the same string its tab carries. */
  readonly range: string;
  /**
   * The two ends of the count, or `null` on a plan that cannot be edited.
   *
   * `PhaseStepView` is the phase editor's own — the value the press would write and the reason it
   * cannot, resolved by `phaseEditGates`. A second derivation here would be a second answer to "may
   * this phase shrink", and the two would disagree the first time a live plan's floor moved.
   */
  readonly decrease: PhaseStepView | null;
  readonly increase: PhaseStepView | null;
}

/**
 * The plan's SCHEDULING SYSTEM used to be stated here, and it is CUT (7 September 2026).
 *
 * `SystemOptionView` / `SystemControlView` and the rail's segmented pair went together: the owner
 * gave the rail's whole width to the six muscle cells, as the programator handoff draws it. The
 * consequence is stated rather than papered over — `self-paced-plan-requirements.md` R1 settles a
 * plan's mode at creation and this was the only surface in the app that said what it was, so the
 * mode is now stated NOWHERE. That is an accepted open item, and its next home is a decision for
 * the owner rather than a spare corner of some other view model.
 *
 * The AVERAGE session length went with it, for the same reason and with the same standing: the
 * projection still computes `averageDurationMinutes`, and no surface words it.
 */

/** One day of the phase, as a cross-day move target for the dock. */
export interface DayMoveTargetView {
  readonly slotToken: string;
  /**
   * 1 (Monday) – 7 (Sunday), and `null` on the TAPE, where a day carries no weekday at all.
   *
   * Its one reader is the dock's exercise drag, which resolves a drop on the WEEK GRID behind the
   * panel back to a day. The tape draws no such target — its tiles report a position, and an
   * exercise dropped on one is not a mechanic that exists — so `null` is the honest answer rather
   * than a fabricated index that would let a lookup match the wrong day.
   */
  readonly weekday: number | null;
  /** "Środa — Pull A", "D3 — Nogi A". The accessible name of a move target, and the announcement. */
  readonly label: string;
  /** "Środa", "D3" — the day's identity alone, which is what a move menu prints. */
  readonly shortLabel: string;
  /** How many main-work blocks it holds — the index a cross-day drop appends at. */
  readonly blockCount: number;
}

/**
 * The two structural acts a workout card's ⋯ offers, already worded — and the reason the copy is
 * refused when it is.
 *
 * **ONE answer for the whole canvas**: every card on screen belongs to the phase in view, and the
 * copy's refusals are phase-scoped facts (the phase's day cap, a phase closed to new days, a week
 * with all seven weekdays taken). A per-card answer would be the same string seven times.
 *
 * Delete carries no refusal since 15 September 2026: a day the client has trained may be deleted on
 * a running plan, as on mobile, and the live confirm says what happens to the history.
 */
export interface DayActionsView {
  /** "Trening" — the group heading over the two entries inside the ⋯ popup. */
  readonly group: string;
  readonly duplicateLabel: string;
  /** Why the copy cannot be made, or `null`. The entry stays, focusable, carrying this. */
  readonly duplicateRefusal: string | null;
  readonly deleteLabel: string;
}

export interface BuilderChromeView {
  readonly planName: string;
  /**
   * "Szablon" / "Plan klienta" — the plan's PROVENANCE, and nothing else. The scheduling mode is
   * `modeChip` below, which is a different fact drawn differently.
   */
  readonly chips: readonly string[];
  /**
   * "Własnym tempem" — the plan's SCHEDULING MODE, or `null` on a calendar plan.
   *
   * The mock draws "Sekwencja" here. The owner settled on 13 September 2026 that one mode carries
   * one name, and the create dialog and `self-paced-plan-requirements.md` had already named it.
   *
   * **This closes an open item rather than adding a decoration.** R1 settles a plan's mode at
   * creation, and when the rail's system control was cut on 7 September 2026 the mode stopped being
   * stated anywhere in this app; `BuilderRail`'s header recorded that as an accepted gap whose next
   * home had to be a decision rather than a spare corner. The 07.09 sequence mock is that decision:
   * a neutral chip in the top bar, behind the plan's name.
   *
   * `null` on the WEEK because the week's own mock draws no mode chip — a calendar plan is the
   * unmarked case, and a chip on both would spend a slot on a fact that is only interesting when it
   * is the unusual one. It is a LABEL, never a switch: nothing on this screen writes the mode.
   */
  readonly modeChip: string | null;
  readonly phases: readonly PhaseTabView[];
  /** The "+ Faza" menu — two empty phases and every existing one as a copy. */
  readonly phaseAdd: PhaseAddMenuView;
  /**
   * The settings popover for the phase in VIEW, or `null` when the plan has no phase there.
   *
   * One editor for the band rather than one per tab: a `tablist` may own only tabs, and the phase
   * the canvas is showing is the phase the trainer is looking at, so there is nothing to pick first.
   */
  readonly phaseEditor: PhaseEditorView | null;
  /**
   * The phase band's right-hand meta. "12 tygodni · 3 fazy" on the grid — the PLAN's span — and
   * "5 treningów w cyklu" on the tape, which is the PHASE's cycle.
   *
   * Two different facts in one slot, and that is the canon's own choice in both specimens: the
   * weekly band has nothing phase-scoped worth the space, and the count of trainings is what a
   * trainer building a cycle checks after every edit. So the canvas supplies the string.
   *
   * The tape's half used to read "Cykl 12 dni · 5 treningów · 7 dni przerwy". Two of those three
   * figures were the rest-day arithmetic R2 forbids, over a cycle R1 says has no length in days.
   */
  readonly bandTotal: string;
  /** The phase in view, by name — the last crumb before the day in the modal's breadcrumb. */
  readonly phaseName: string;
  /**
   * The rail's six volume bars, judged against MEV / MAV / MRV.
   *
   * It REPLACES `load` (six share bars) and `loadCaption` (the sentence naming the scope and the
   * 1.0 / 0.5 weighting), which the 07.09 programator handoff draws no room for: the rail's caption
   * block is now "MEV → MAV → MRV" over "serie tygodniowo", and the percentage share the old bars
   * carried has no cell to sit in. Two things went with them and are recorded rather than lost —
   * the week's per-group SHARE, which the day card's own module still prints, and the weighting
   * note, which is now stated nowhere on this screen. Flagged for the owner.
   *
   * Always present, under both canvases. It used to be `null` on a sequence phase whose every day
   * the save would drop, which made the whole band unmount and remount during ordinary editing —
   * see `volumeStatus`'s header for the measurement and the withdrawal. A cycle with nothing in it
   * now keeps its band and states nothing in it.
   */
  readonly volume: VolumeRailView;
  /**
   * "Plan przypisany · tyg. 1–4 zrealizowane", or just "Plan przypisany" before the client has
   * begun. `null` only on a plan no client is on.
   */
  readonly lock: string | null;
  /**
   * The keyboard instruction a lifted card points its `aria-describedby` at. One string for the
   * whole canvas: a description repeated on seven cards is one sentence, not seven.
   */
  readonly moveHint: string;
  /** What the live region says when Escape drops a lift. One string for the canvas. */
  readonly moveCancelled: string;
  /** "Sesja w ruchu" — the canon's `.slot.is-source` label, printed over the card being dragged. */
  readonly moveSourceLabel: string;
  /**
   * Why no new training may be added to the phase in view, or `null`. One answer for the canvas,
   * because both causes (`dayAdditionRefusal`) are facts about the phase. The add affordance stays
   * rendered and focusable and carries this as its reason — view ADR §12.
   */
  readonly addDayRefusal: AddDayRefusalView | null;
  /** Duplicate and Delete, as the card's ⋯ offers them. See `DayActionsView`. */
  readonly dayActions: DayActionsView;
  /** The phase's programmed days, worded — the dock's "Przenieś do dnia…" targets. */
  readonly dayTargets: readonly DayMoveTargetView[];
}

/**
 * Why the phase takes no new training, at two lengths.
 *
 * The two exist because the two canvases have two very different amounts of room for the same fact.
 * The SEQUENCE tape prints it under one add tile that spans the row, so it prints `full`. The WEEK
 * grid has one tile per empty weekday, in a column about 80px wide, so it prints `short` and carries
 * `full` where the room is unlimited: the tile's tooltip, its description for assistive technology,
 * and the sentence the canvas announces on a refused press.
 *
 * One object rather than two nullable fields, so no caller can draw half of the state.
 */
export interface AddDayRefusalView {
  /** "Faza zamknięta" — two words, ours, for a column that cannot hold a sentence. */
  readonly short: string;
  /** The whole reason. Mobile's own words where the cause is a lived phase. */
  readonly full: string;
}

/**
 * The sentence a phase closed to new days owes the trainer — mobile's own, from the shared locale,
 * in the plan's unit noun. Shared by the add affordance and the copy, because a copy IS a new day.
 */
function closedToNewDaysWords(t: Translate, draft: PlanDraft): string {
  return t(
    draft.mode === 'selfpaced'
      ? 'plans.builder.addDayPhaseLivedNoteCycle'
      : 'plans.builder.addDayPhaseLivedNote',
  );
}

/**
 * Why a training may not be added to this phase, worded, or `null`.
 *
 * The cap reads the tape's own sentence, which names the number; the week grid never reaches it,
 * because seven weekday columns hold at most seven days. Each cause carries its own two-word form
 * as well — see `AddDayRefusalView` for what the two lengths are for.
 */
export function addDayRefusalWords(
  t: Translate,
  draft: PlanDraft,
  phaseIndex: number,
): AddDayRefusalView | null {
  const refusal = dayAdditionRefusal(draft, phaseIndex);
  if (refusal === null) return null;
  return refusal === 'closedToNewDays'
    ? { short: t(`${KEY}.addDayClosedShort`), full: closedToNewDaysWords(t, draft) }
    : {
        short: t(`${KEY}.addDayFullShort`),
        full: t(`${KEY}.tape.addAtCap`, { count: COLLECTION_CAPS.daysPerPhase.max }),
      };
}

/**
 * The two day acts, worded for the phase in view.
 *
 * The copy's refusal is asked with the phase's FIRST day as the address, because the entity's
 * predicate takes one — and every one of its answers is a fact about the PHASE (its length, its
 * week, its closed cycles), so any day of it gives the same answer. A phase with no day at all draws
 * no card, so there is nothing for the words to sit on and the refusal reads `null`.
 */
function toDayActions(t: Translate, draft: PlanDraft, phaseIndex: number): DayActionsView {
  const anyDay = draft.phases[phaseIndex]?.days[0]?.slotToken ?? null;
  const duplication = anyDay === null ? null : dayDuplicationRefusal(draft, anyDay);
  return {
    group: t(`${KEY}.dayActions.group`),
    duplicateLabel: t(`${KEY}.dayActions.duplicate`),
    duplicateRefusal:
      duplication === null
        ? null
        : duplication === 'closedToNewDays'
          ? closedToNewDaysWords(t, draft)
          : t(`${KEY}.dayActions.refusal.${duplication}`),
    deleteLabel: t(`${KEY}.dayActions.delete`),
  };
}

function toPhaseTabs(
  t: Translate,
  draft: PlanDraft,
  phases: readonly PhaseSummary[],
  activeIndex: number,
): PhaseTabView[] {
  const unit = unitNoun(t, draft);
  return phases.map((phase) => {
    const isActive = phase.index === activeIndex;
    const from = phase.startCycle;
    const to = phase.startCycle + phase.cycleCount - 1;
    return {
      index: phase.index,
      name: phase.name,
      // A SELF-PACED phase names the unit in FULL and declines it — the canon prints "cykle 1–3" and
      // "cykl 7", not "cykl 1–3". Polish inflects the noun on the count, so a single-cycle phase and
      // a range need different words and i18next's own plural suffixes are what resolve them. The
      // weekly band abbreviates instead ("tyg. 1–4"), which is what keeps three tabs on one row and
      // is also why the two cannot share a key.
      range:
        draft.mode === 'selfpaced'
          ? t(`${KEY}.phaseRangeCycles`, { count: to - from + 1, from, to })
          : t(`${KEY}.phaseRange`, { unit, from, to }),
      tag: t(`${KEY}.phaseType.${phase.type}`),
      isActive,
      // The DELOAD marker wins, even on the active tab. Blue is a structural label — what kind of
      // phase this is — and the accent is a state, so letting the state overwrite the label made
      // the one phase whose kind matters most stop announcing it at exactly the moment the trainer
      // opened it. Nothing is lost: the active tab is already carried by its background, its border
      // and its weight, so the pip was never the only signal. A build phase in view still takes the
      // accent, so the screen keeps exactly one.
      pip: phase.type === 'deload' ? 'deload' : isActive ? 'accent' : 'neutral',
    };
  });
}

export interface BuilderChromeInput {
  readonly draft: PlanDraft;
  readonly phases: readonly PhaseSummary[];
  readonly phase: PhaseSummary;
  /** The muscle rollup over the phase's days — the same scope in both canvases. */
  readonly load: MuscleLoad;
  /** The phase band's right-hand meta — see `BuilderChromeView.bandTotal`. */
  readonly bandTotal: string;
  /** The dock's cross-day targets, already worded by the canvas that knows the coordinates. */
  readonly dayTargets: readonly DayMoveTargetView[];
  /**
   * How many days the phase's cycle runs — `model/cycleDays.ts`'s answer on the SEQUENCE canvas,
   * and `null` on the week, where a week's load needs no conversion to a week.
   *
   * The canvas supplies it rather than this module deriving it, for the reason every other
   * canvas-shaped difference here is a parameter: only the canvas knows what it is counting, and a
   * second derivation would be a second answer to the figure the pattern row already prints.
   */
  readonly cycleLengthDays: number | null;
  /**
   * The exercise catalogue read has not answered yet, so no muscle cell may state a number.
   *
   * It is a SECOND read behind the plan's own, and its absence is indistinguishable from a week
   * that trains nothing: `toMuscleAttribution` over an empty catalogue makes every group zero. The
   * rail waits for it — `CLAUDE.md`, a section waits for all of its reads — and only the rail does:
   * every other surface here renders from the draft alone, and the standing rule that "the
   * catalogue's failure is not the plan's failure" is unchanged.
   */
  readonly isAttributionPending: boolean;
  readonly t: Translate;
  readonly locale: string;
}

export function toBuilderChrome({
  draft,
  phases,
  phase,
  load,
  bandTotal,
  dayTargets,
  cycleLengthDays,
  isAttributionPending,
  t,
  locale,
}: BuilderChromeInput): BuilderChromeView {
  const unit = unitNoun(t, draft);
  const lock = livePlanLock(draft.live);

  return {
    planName: draft.name.trim() || t(`${KEY}.untitledPlan`),
    chips: [t(`${KEY}.origin.${draft.isLibraryEntry ? 'template' : 'clientPlan'}`)],
    modeChip: draft.mode === 'selfpaced' ? t(`${KEY}.mode.selfPaced`) : null,
    phases: toPhaseTabs(t, draft, phases, phase.index),
    phaseAdd: toPhaseAddMenu(t, draft, phases),
    phaseEditor: toPhaseEditor(t, draft, phase.index, isLivePlan(draft.live)),
    bandTotal,
    phaseName: phase.name,
    // The tape reaches the WEEKLY landmarks by conversion since 7 September 2026, and the rail
    // states nothing at all until the exercise catalogue has answered. Both are `volumeStatus`'s
    // to explain; what is handed over here is the phase's days (the cycle the conversion divides
    // by) and whether that second read is still open.
    volume: toVolumeRail(t, locale, load, {
      isPending: isAttributionPending,
      cycleLengthDays,
    }),
    // Two sentences, because there are two facts. A plan the client has walked into names the
    // cycles that are frozen; one they have not begun has no range to name and says only that it is
    // assigned — which is the part that was missing entirely, and the part a self-paced plan can sit
    // on for weeks.
    lock:
      lock === null
        ? null
        : lock.consumedCycles <= 0
          ? t(`${KEY}.lockAssigned`)
          : t(`${KEY}.lock`, { unit, from: 1, to: lock.consumedCycles }),
    moveHint: t(`${KEY}.dnd.dayGripHint`),
    moveCancelled: t(`${KEY}.dnd.cancelled`),
    moveSourceLabel: t(`${KEY}.dnd.sourceSlot`),
    dayTargets,
    addDayRefusal: addDayRefusalWords(t, draft, phase.index),
    dayActions: toDayActions(t, draft, phase.index),
  };
}
